/**
 * What a composed Source Profile is called.
 *
 * **It lives here rather than in the Add form because two screens have to
 * agree about it, and the one time they did not it published a contradiction.**
 * A PFF profile created for FCS was called *PFF — Player Statistics (FBS, Reg +
 * Post)*: the name was suggested once, from the division the form happened to
 * be showing when the fields were fetched, and changing the division afterwards
 * moved the League, moved every Feed address and left the name standing. The
 * Sources list then showed `fcs` in the League column beside `FBS` in the
 * Source column, and nothing on the row said which of the two the published
 * file would be named after.
 *
 * So the name is *derived* from the parameters, and a screen's job is to notice
 * whether the one on the profile is still the derived one. `matchesComposed` is
 * that question, and it is what keeps a name somebody typed from being
 * overwritten by a recompose — a suggestion may be replaced, a decision may not.
 *
 * Takes the parameters rather than the template, exactly as
 * `transferPortalNamePlaceholder` does, so that *which* parameters are in play
 * stays the caller's decision: applicability is answered once by
 * `applicableParameters` in `api.ts` and not a second time here. It also keeps
 * this module free of any runtime import, which is what lets `npm test` run it
 * — bare `node --experimental-strip-types`, with no bundler to resolve a
 * extensionless path.
 */

import type { TemplateParameter, TemplateValues } from "./api";

/**
 * The publisher, plus the choices that tell two of its profiles apart.
 *
 * "PFF — Player Statistics (FBS, Reg + Post)". A template composes several
 * profiles that differ only by their parameters, so naming them all after the
 * publisher makes the dashboard unreadable — eighteen sources where four are
 * called the same thing.
 *
 * Single choices only. A season is in its own column on every screen that shows
 * this, a list of reports is thirteen labels long, and a number is not a name.
 *
 * A value no option declares contributes nothing rather than itself: PFF's
 * `ncaa-d2` is a slug, `D2` is the word for it, and a name is worth having only
 * in the publisher's own words.
 */
export function composedSourceName(
  publisher: string,
  parameters: readonly TemplateParameter[],
  values: TemplateValues,
): string {
  const distinguishing = parameters
    .filter((parameter) => parameter.kind === "single_choice")
    .map((parameter) => {
      const value = String(values[parameter.key] ?? "");
      return parameter.options.find((option) => option.value === value)?.label;
    })
    .filter(Boolean);
  return distinguishing.length > 0 ? `${publisher} (${distinguishing.join(", ")})` : publisher;
}

/**
 * Whether `name` is still the name these parameters compose.
 *
 * The permission to rewrite it. True means nobody has renamed this source since
 * it was suggested, so following the parameters restores the agreement the name
 * was created with; false means somebody typed something, and a form that
 * silently replaces a typed name is a form that discards an edit.
 *
 * Whitespace is ignored, because the name field trims on save and the
 * suggestion does not. An empty name has never been anybody's decision, so it
 * counts as the form's to fill.
 */
export function matchesComposed(
  name: string,
  publisher: string,
  parameters: readonly TemplateParameter[],
  values: TemplateValues,
): boolean {
  const trimmed = name.trim();
  if (trimmed === "") return true;
  return trimmed === composedSourceName(publisher, parameters, values).trim();
}
